import { z } from "zod";
import { iframeActions } from "rtr-constants";

const billingPayloadValuesSchema = z.object({
  billingAddressOption: z.string().optional(),
  alreadySet: z.boolean().optional(),
  firstName: z.string(),
  lastName: z.string(),
  street1: z.string(),
  street2: z.string().optional(),
  city: z.string().optional(),
  phone: z.string().optional(),
  postalCode: z.string().optional(),
  state: z.string().optional(),
  zoneCode: z.string().optional(),
  userId: z.union([z.string(), z.number()]).optional(),
});

const onChangeAndBlurCallbackSchema = z.object({
  value: z.literal("SANITIZED_VALUE_IN_IFRAME"),
  isValid: z.union([z.boolean(), z.null()]),
  name: z.string(),
  inputObj: z
    .object({
      name: z.string(),
      id: z.string().optional(),
      value: z.string().optional(),
      error: z.string().optional(),
      required: z.boolean().optional(),
      isValid: z.union([z.boolean(), z.null()]).optional(),
      autoComplete: z.string().optional(),
      type: z.string().optional(),
      additionalClassName: z.string().optional(),
      disabled: z.boolean().optional(),
      readOnly: z.boolean().optional(),
      label: z.string().optional(),
      validationErrorMsg: z.string().optional(),
      maxLength: z.number().optional(),
      focus: z.boolean().optional(),
      defaultValue: z.string().optional(),
      isRequired: z.boolean().optional(),
      tabIndex: z.number().optional(),
    })
    .passthrough(), // Allow additional properties since inputObj can contain other merged props
});

const paymentMethodsSchema = z.array(
  z.object({
    type: z.string(),
    id: z.number(),
    cardType: z.string().optional(),
    expiration: z.string().optional(),
    firstName: z.string().optional(),
    lastFourDigits: z.string().optional(),
    lastName: z.string().optional(),
  })
);

const fullPaymentMethodSchema = z.array(
  z.object({
    cardNumber: z.string(),
    cardType: z.string(),
    ccv: z.string(),
    default: z.boolean().optional(),
    expiration: z.string(),
    expirationNumber: z.string(),
    expirationText: z.string(),
    firstName: z.string(),
    id: z.string(),
    lastFourDigits: z.string(),
    lastName: z.string(),
    name: z.string().optional(),
    recurring: z.boolean().optional(),
    subType: z.string(),
    userId: z.number(),
    user: z.object({
      type: z.string(),
      id: z.string(),
      email: z.string(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    }),
  })
);

export const validActions = {
  [iframeActions.PARENT_COMPONENT_PROPS_UPDATED]: {
    name: "parentComponentPropsUpdated",
    schema: z.object({
      errors: z.record(z.string()),
      formIsValid: z.boolean(),
    }),
  },
  [iframeActions.SAVE_PAYMENT_METHOD_USING_EPROTECT]: {
    name: "savePaymentMethodUsingEprotect",
    schema: z.object({
      orderId: z.union([z.string(), z.number()]),
      id: z.union([z.string(), z.number()]),
      paymentMethodUrl: z.string(),
      billingPayloadValues: billingPayloadValuesSchema,
    }),
  },
  [iframeActions.SAVE_APPLE_PAY_PAYMENT_TOKEN_USING_EPROTECT]: {
    name: "saveApplePayPaymentTokenUsingEprotect",
    schema: z.object({
      orderId: z.union([z.string(), z.number()]),
      id: z.union([z.string(), z.number()]),
      paymentMethodUrl: z.string(),
      billingPayloadValues: billingPayloadValuesSchema,
      PKPaymentToken: z.object({
        version: z.string(),
        data: z.string(),
        signature: z.string(),
        header: z.object({
          applicationData: z.string().optional(),
          transactionId: z.string(),
          ephemeralPublicKey: z.string(),
          publicKeyHash: z.string(),
        }),
      }),
    }),
  },
  [iframeActions.ON_SAVE_APPLE_PAY_PAYMENT_METHOD_SUCCESS]: {
    name: "onSaveApplePayPaymentMethodSuccess",
    schema: z.object({
      id: z.union([z.string(), z.number()]),
    }),
  },
  [iframeActions.HookNames.ON_SAVE_PAYMENT_METHOD_SUCCESS]: {
    /**
     * this is a mess of `.optional()`s, and the reason for this is because sometimes this hook
     * is used to pass a payload of a Checkout response, and sometimes it's not. just for
     * cleanliness, this should be probably be updated so it uses two separate hooks so we
     * can keep to strict validation.
     *
     * Ticket to remember this: CE-3668
     */
    name: "onSavePaymentMethodSuccess",
    schema: z.union([
      z.object({
        id: z.string(),
        bagItems: z
          .array(
            z.object({
              type: z.string(),
              createdAt: z.string(),
              id: z.string(),
              sku: z.string(),
              membershipTierId: z.number().optional(),
            })
          )
          .optional(),
        charges: z
          .object({
            chargeErrors: z.array(z.unknown()),
            itemTotals: z.record(
              z.object({
                grand: z.string(),
                insurance: z.string(),
                preDiscountedInsurance: z.string(),
                prePromoSub: z.string(),
                promoItem: z.string(),
                sub: z.string(),
                tax: z.string(),
              })
            ),
            totals: z.object({
              grand: z.string(),
              insurance: z.string(),
              preDiscountedInsurance: z.string(),
              preDiscountedShipping: z.string(),
              prePromoSub: z.string(),
              promoItem: z.string(),
              shipping: z.string(),
              slotProration: z.string(),
              sub: z.string(),
              tax: z.string(),
            }),
          })
          .optional(),
        checkoutStatus: z.string().optional(),
        checkoutType: z.string().optional(),
        expectedPayments: z.record(z.string()).optional(),
        paymentMethods: paymentMethodsSchema.optional(),
        shippingAddress: z
          .object({
            city: z.string(),
            firstName: z.string(),
            id: z.number(),
            lastName: z.string(),
            phone: z.string(),
            postalCode: z.string(),
            street1: z.string(),
            street2: z.string(),
            zoneCode: z.string(),
          })
          .optional(),
        userId: z.number().optional(),
      }),
      fullPaymentMethodSchema,
    ]),
  },
  [iframeActions.ON_SAVE_APPLE_PAY_PAYMENT_METHOD_FAILURE]: {
    name: "onSaveApplePayPaymentMethodFailure",
    schema: z.object({
      tokenOrderId: z.string(),
      eProtectResponse: z
        .object({
          error: z.string().optional(),
          message: z.string().optional(),
          status: z.string().optional(),
          code: z.string().optional(),
        })
        .optional(),
      object_type: z.string().optional(),
      action: z.string().optional(),
    }),
  },
  [iframeActions.LOG_ACTION]: {
    name: "logAction",
    schema: z.object({
      tokenOrderId: z.string().optional(),
      object_type: z.string().optional(),
      action: z.string().optional(),
    }),
  },
  [iframeActions.ON_CHANGE_CALLBACK]: {
    name: "onChangeCallback",
    schema: onChangeAndBlurCallbackSchema,
  },
  [iframeActions.ON_BLUR_CALLBACK]: {
    name: "onBlurCallback",
    schema: onChangeAndBlurCallbackSchema,
  },
  [iframeActions.HookNames.BEFORE_EPROTECT_TOKEN_GENERATION]: {
    name: "beforeEprotectTokenGeneration",
    schema: z.object({}).optional(),
  },
};
